import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Chart from 'react-apexcharts';
import moment from 'moment';
import { Grid, InputLabel, TextField, FormControl, Select, MenuItem, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LoadingButton from '@mui/lab/LoadingButton';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ReportFileExports from 'components/Reports/FileExports';
import MainCard from 'components/Cards/MainCard';
import SubCard from 'components/Cards/SubCard';
import ApiHelper from 'utils/apiHelper';
import { gridSpacing } from 'utils/constants';
import { convertDate } from 'utils/commonHelpers';
import BatteryHealthDevicesDialog from './BatteryHealthDevicesDialog';
import ReportVisualization from 'components/Reports/ReportVisualization';

const ReportsBatteryHealth = () => {
    const theme = useTheme();
    const currentDate = new Date();
    const today = new Date();
    const [startDate, setStartDate] = useState(currentDate.setMonth(currentDate.getMonth() - 1));
    const [endDate, setEndDate] = useState(today.setDate(today.getDate() - 1));
    const [groupId, setGroupId] = useState(0);
    const [groupsData, setGroupsData] = useState([]);
    const [batteryHealthData, setBatteryHealthData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [showDevices, setShowDevices] = useState(false);
    const [columnIndex, setColumnIndex] = useState(0);
    const chartLabels = ['0-50%', '50-60%', '60-70%', '70-80%', '80-90%', '90-100%'];
    const [viewData, setViewData] = useState([]);
    const [viewId, setViewId] = useState(0);
    const [viewStatements, setViewStatements] = useState([]);
    const [selectedStatements, setSelectedStatements] = useState([]);

    const [filters, setFilters] = useState({});

    const initialViewData = async (selectedItem) => {
        setLoading(true);
        try {
            // Get views list and statements
            const [views, viewStatements] = await Promise.all([
                ApiHelper.get('/api/management/views/'),
                ApiHelper.get('/api/management/get_view_statements/')
            ]);

            // Only proceed if we have views
            if (views.data.res.length > 0) {
                // Update views list and statements
                setViewData(views.data.res);
                setViewStatements(viewStatements.data);
            }
        } catch (err) {
            if (err.response?.status !== 401) {
                toast.error('Failed to fetch views data');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getGroups();
        initialViewData();
    }, []);

    const getGroups = () => {
        ApiHelper.get('/api/reports/groups/')
            .then((res) => {
                setGroupsData(res.data);
            })
            .catch();
    };

    const getBatteryHealthData = () => {
        if (convertDate(startDate) === 'Invalid date') {
            toast.error('Fail! The start date format is incorrect');
            return true;
        }
        if (convertDate(endDate) === 'Invalid date') {
            toast.error('Fail! The end date format is incorrect');
            return true;
        }
        if (moment(convertDate(startDate)) > moment(convertDate(endDate))) {
            toast.error('Fail! The start date can not be greater than the end date');
            return true;
        }
        setLoading(true);

        // Get selected view statements
        const selectedStatements = viewStatements.filter((statement) => statement.view_id === viewId - 1);

        // Format statements for API call
        const statement = selectedStatements.map((statement, index) => ({
            id: index + 1,
            attribute: statement.deviceattrib,
            negative: statement.negative === 1,
            operator: statement.operator_id,
            value: statement.value,
            connector: index === selectedStatements.length - 1 ? '1' : statement.connector_id
            // operatorOptions: commonData.operators
        }));

        setSelectedStatements(statement);

        ApiHelper.post('/api/reports/battery_health_reports/', {
            start_date: convertDate(startDate),
            end_date: convertDate(endDate),
            group_id: groupId === 0 ? '' : groupId.toString(),
            statement: statement.length > 0 ? statement : []
        })
            .then((res) => {
                setBatteryHealthData(res.data.values);
                //setDevicesUsageLabels(res.data.labels);
                setLoading(false);
                toast.success('Successfully done');
                let groupName = 'Any';
                for (let i = 0; i < groupsData.length; i++) {
                    if (groupsData[i].id === groupId) {
                        groupName = groupsData[i].name;
                    }
                }
                let viewName = 'Any';
                if (viewId !== 0) {
                    for (let i = 0; i < viewData.length; i++) {
                        if (viewData[i].id === viewId - 1) {
                            viewName = viewData[i].name;
                        }
                    }
                }
                const filterData = [
                    ['Start Date', convertDate(startDate)],
                    ['End Date', convertDate(endDate)],
                    ['Group Name', groupName],
                    ['View Name', viewName]
                ];
                setFilters({
                    start_date: convertDate(startDate),
                    end_date: convertDate(endDate),
                    group_id: groupId === 0 ? '' : groupId.toString(),
                    statement: statement.length > 0 ? statement : [],
                    report: 'Battery Health',
                    filterData: filterData
                });
            })
            .catch(() => {
                setLoading(false);
                toast.warning('There is no battery health statistics data');
            });
    };

    const series = [
        {
            name: 'Number of Devices',
            data: batteryHealthData
        }
    ];
    const options = {
        chart: {
            toolbar: {
                show: false
            },
            events: {
                dataPointSelection: (event, chartContext, config) => {
                    // this will print mango, apple etc. when clicked on respective datapoint
                    setShowDevices(true);
                    setColumnIndex(config.dataPointIndex);
                    // const index = batteryHealthData.findIndex((data) => data.columnnumber === config.dataPointIndex);
                    // setSelectedDevices(batteryHealthData[index]['devices']);
                },
                dataPointMouseEnter: (event) => {
                    event.target.style.cursor = 'pointer';
                }
            }
        },
        plotOptions: {
            bar: {
                distributed: true
            }
        },
        xaxis: {
            categories: chartLabels,
            title: {
                text: 'Battery Charge Percentile'
            }
        },
        yaxis: {
            title: {
                text: 'Number of devices'
            }
        },
        colors: ['#A13426', '#A15D27', '#A19A27', '#26A125', '#25A167', '#259BA1']
    };

    return (
        <MainCard title="Operational Reports Management" cardHeaderTitle="Battery Health Statistics" cardHeaderShow={true}>
            <Grid container spacing={gridSpacing}>
                <Grid item xs={10}>
                    <Typography sx={{ fontSize: '16px' }}>
                        Provides a bar chart showing devices ranked in percentiles by battery charge capacity, plus drill-down to the device
                        details.
                    </Typography>
                </Grid>
                {batteryHealthData.length > 0 && (
                    <Grid item xs={2}>
                        <ReportFileExports
                            downloadFileName="battery_health_report"
                            downloadFileOption="all"
                            data={[batteryHealthData]}
                            headers={chartLabels}
                            filters={filters}
                            setExportLoading={setExportLoading}
                        />
                    </Grid>
                )}
                <Grid item xs={3}>
                    <SubCard title="Filters">
                        <Grid container>
                            <Grid item xs={12}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="Start Date"
                                        value={startDate}
                                        maxDate={endDate}
                                        onChange={(newValue) => {
                                            setStartDate(newValue);
                                        }}
                                        renderInput={(params) => <TextField {...params} sx={{ width: '100%' }} />}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 4 }}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="End Date"
                                        value={endDate}
                                        maxDate={today}
                                        onChange={(newValue) => {
                                            setEndDate(newValue);
                                        }}
                                        renderInput={(params) => <TextField {...params} sx={{ width: '100%' }} />}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 4 }}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Select Group</InputLabel>
                                    <Select
                                        label="Select Group"
                                        value={groupId}
                                        name="group"
                                        onChange={(e) => setGroupId(Number(e.target.value))}
                                    >
                                        <MenuItem value={0}>Any</MenuItem>
                                        {groupsData.length > 0 &&
                                            groupsData.map((data, key) => (
                                                <MenuItem value={data.id} key={key}>
                                                    {data.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 4 }}>
                                <FormControl sx={{ width: '100%' }}>
                                    <InputLabel>Select View</InputLabel>
                                    <Select
                                        label="Select View"
                                        value={viewId}
                                        name="view"
                                        onChange={(e) => setViewId(Number(e.target.value))}
                                    >
                                        <MenuItem value={0}>Any</MenuItem>
                                        {viewData.length > 0 &&
                                            viewData.map((data, key) => (
                                                <MenuItem value={data.id + 1} key={key}>
                                                    {data.name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sx={{ mt: 4 }}>
                                <LoadingButton
                                    color="primary"
                                    variant="contained"
                                    sx={{ width: '100%' }}
                                    loading={loading}
                                    onClick={getBatteryHealthData}
                                >
                                    Submit
                                </LoadingButton>
                            </Grid>
                        </Grid>
                    </SubCard>
                </Grid>
                <Grid item xs={9}>
                    <ReportVisualization
                        loading={loading}
                        data={batteryHealthData}
                        type="bar"
                        options={options}
                        series={series}
                        width="80%"
                    />
                    {showDevices && (
                        <BatteryHealthDevicesDialog
                            columnIndex={columnIndex}
                            startDate={convertDate(startDate)}
                            endDate={convertDate(endDate)}
                            groupId={groupId}
                            groupsData={groupsData}
                            xLabel={chartLabels[columnIndex]}
                            onClose={() => setShowDevices(false)}
                            selectedStatements={selectedStatements}
                        />
                    )}
                </Grid>
            </Grid>
            {exportLoading === true ? (
                <div className="overlayStyle">
                    <div className="spinner spinnerStyle"></div>
                </div>
            ) : null}
        </MainCard>
    );
};

export default ReportsBatteryHealth;
